// A basilar clot's PCom anatomy (ba-blood's v5 pair, #665; the switch itself is ba-app's, efc65b61): what the
// chosen anatomy and arm come to, read from the loaded tissue run's own growth table rather than the handoff's
// numbers, so the line follows whatever run the switch resolves. In v5 the traced pair has no ischaemic tissue
// at all, so its chart would be empty and the line says so in words.

import { TISSUE_STATES, tissueAt } from '../app/stroke.ts';
import type { GrowthTable } from '../data/sims/hemo.ts';

export interface Outcome {
  /** mL at the run's last row. */
  core: number;
  penumbra: number;
  saved: number;
  /** The last row's time, s. */
  end: number;
  /** No tissue state above zero anywhere in the run. */
  empty: boolean;
}

/** Whether no tissue state is ever above zero over the run (its chart would have nothing to draw). */
export function isEmptyGrowth(table: GrowthTable): boolean {
  const cols = TISSUE_STATES.map((s) => s.col).filter((c) => table.values[c]);
  return cols.every((c) => table.values[c]!.every((x) => !(x > 0)));
}

/** The run's end state from its growth table; null for a table without rows. */
export function outcomeOf(table: GrowthTable): Outcome | null {
  const n = table.time.length;
  if (!n) return null;
  const end = table.time[n - 1]!;
  const at = tissueAt(table, end);
  const ml = (col: string) => at.find((x) => x.col === col)?.ml ?? 0;
  return { core: ml('core_ml'), penumbra: ml('penumbra_ml'), saved: ml('reperfused_saved_ml'), end, empty: isEmptyGrowth(table) };
}

const mL = (x: number) => `${Math.round(x)} mL`;

/** One sentence for the switch: the anatomy, then what the run comes to at its end for the arm chosen. */
export function outcomeLine(o: Outcome, anatomy: 'segmented' | 'traced', hours: number | null): string {
  const who = anatomy === 'traced' ? 'With the traced PComs' : 'As segmented';
  const when = `${Math.round(o.end / 3600)} h`;
  if (o.empty) return `${who}: no penumbra or core in this run, through ${when}.`;
  if (hours) return `${who}: ${mL(o.saved)} of penumbra saved by reperfusion at ${hours} h; core ${mL(o.core)} at ${when}.`;
  return `${who}: core ${mL(o.core)} and penumbra ${mL(o.penumbra)} at ${when}, untreated.`;
}
